import { z } from 'zod';
import { userRoleSchema } from '../enums.js';

/**
 * Password policy: length is the dominant factor in resistance to offline
 * cracking, so we require length rather than a character-class checklist, which
 * mostly pushes people toward predictable substitutions like `P@ssw0rd`.
 */
export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(200, 'Password must be at most 200 characters');

/**
 * Normalise before validating, not after: `z.email().trim()` would reject
 * " ada@example.com " for the whitespace it was about to remove.
 */
export const emailSchema = z.string().trim().toLowerCase().pipe(z.email().max(254));

export const registerRequestSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  displayName: z.string().trim().min(1).max(60),
  /** IANA timezone; streaks are computed against the learner's local date. */
  timezone: z.string().max(64).default('UTC'),
});
export type RegisterRequest = z.infer<typeof registerRequestSchema>;

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const publicUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string(),
  role: userRoleSchema,
  timezone: z.string(),
  createdAt: z.string(),
});
export type PublicUser = z.infer<typeof publicUserSchema>;

export const authResponseSchema = z.object({
  user: publicUserSchema,
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

/**
 * "Is anyone signed in?" — answered without an error.
 *
 * `/auth/me` returns 401 when nobody is, which is correct for a protected
 * resource but wrong for a *question*: the SPA asks it on every page load, so
 * every anonymous visitor gets a red 401 in their browser console for a state
 * that is entirely normal. This endpoint asks the same thing and answers 200
 * with a null user.
 */
export const sessionResponseSchema = z.object({
  user: publicUserSchema.nullable(),
});
export type SessionResponse = z.infer<typeof sessionResponseSchema>;

/**
 * Whether anyone may sign themselves up right now.
 *
 * Public, and deliberately so — the sign-up page has to know before it can
 * render, and there is nothing sensitive in the answer. When this is closed the
 * register endpoint still exists but requires an administrator, so accounts can
 * be created deliberately without reopening the door.
 */
export const registrationStatusSchema = z.object({
  open: z.boolean(),
});
export type RegistrationStatus = z.infer<typeof registrationStatusSchema>;
